// Org slug extraction from hostname for subdomain-based multi-tenancy.
//
// Production: {org-slug}.vista-cloud.io → slug = "stark-industries"
// Dev:        localhost:5173             → slug = null (use normal flow)
//
// The slug is sent to POST /auth/session so the API can validate
// the user belongs to the org matching the subdomain.

const PRODUCT_DOMAIN = import.meta.env.VITE_PRODUCT_DOMAIN || 'vista-cloud.io';

// Reserved slugs — infrastructure subdomains that cannot be org slugs.
// SYNC: also defined in cloud/workers/src/lib/validate.js,
//       cloud/workers/src/lib/slugify.js, and
//       cloud/admin/workers/src/lib/slugify.js.
const RESERVED_SLUGS = new Set([
  'admin', 'api', 'www', 'app', 'mail', 'ftp',
  'staging', 'dev', 'test', 'status', 'docs',
]);

// Slug format rules — must match cloud/workers/src/lib/slugify.js and
// cloud/admin/workers/src/lib/slugify.js exactly so client-side preview
// validation matches server-side acceptance.
const SLUG_MIN_LENGTH = 2;
const SLUG_MAX_LENGTH = 32;
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Extract the org slug from the current hostname.
 * Returns null in dev (localhost) or if hostname doesn't match the product domain.
 * @param {string} [hostname] - Override hostname for testing (defaults to window.location.hostname)
 */
export function getOrgSlug(hostname) {
  hostname = hostname || window.location.hostname;

  // Dev: localhost or IP — no subdomain routing
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
    return null;
  }

  // Must be a subdomain of the product domain: {slug}.vista-cloud.io
  if (!hostname.endsWith(`.${PRODUCT_DOMAIN}`)) {
    return null;
  }

  const slug = hostname.slice(0, -(PRODUCT_DOMAIN.length + 1));

  // Must be a single label (no nested subdomains like "a.b.vista-cloud.io")
  if (!slug || slug.includes('.')) {
    return null;
  }

  // Reserved slugs are not org subdomains
  if (RESERVED_SLUGS.has(slug)) {
    return null;
  }

  return slug;
}

/**
 * Check if a slug is reserved (for org creation validation).
 */
export function isReservedSlug(slug) {
  return RESERVED_SLUGS.has(slug);
}

/**
 * Is the current host the product-domain apex (e.g. `vista-cloud.io`),
 * i.e. neither a slug subdomain nor a local dev host?
 *
 * The apex is intentionally not behind CF Access, so landing there
 * would otherwise cause the normal auth flow to fail with a redirect
 * loop. The App.jsx short-circuits to a public landing page when
 * this returns true. Localhost/dev returns false so the normal auth
 * flow runs.
 */
export function isApexHost(hostname) {
  hostname = hostname || window.location.hostname;
  return hostname === PRODUCT_DOMAIN;
}

/**
 * Derive a slug from a display name (best-effort, client-side preview).
 * Mirrors the server's normalizeSlug() — both must produce the same
 * output for the same input so the admin Create-Org form's live preview
 * matches what the Worker would persist if the operator accepts it.
 */
export function normalizeSlug(input) {
  if (typeof input !== 'string') return '';
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Validate a slug client-side. Returns null when valid; otherwise a
 * full-sentence human-readable error string. Strings match the server's
 * validateSlug() byte-for-byte (enforced by slugify-mirror.test.js in
 * the admin Workers package) so the same message is shown whether the
 * failure is caught client-side or returned by the Worker. vista#339
 * review note 2.
 */
export function validateSlug(slug) {
  if (typeof slug !== 'string' || slug.length === 0) {
    return 'slug is required';
  }
  if (slug.length < SLUG_MIN_LENGTH) {
    return `slug must be at least ${SLUG_MIN_LENGTH} characters`;
  }
  if (slug.length > SLUG_MAX_LENGTH) {
    return `slug must be at most ${SLUG_MAX_LENGTH} characters`;
  }
  if (!SLUG_PATTERN.test(slug)) {
    return 'slug must contain only lowercase letters, digits, and hyphens; no leading, trailing, or consecutive hyphens';
  }
  if (RESERVED_SLUGS.has(slug)) {
    return `"${slug}" is a reserved name and cannot be used as an organization slug`;
  }
  return null;
}

export { RESERVED_SLUGS };
